import { Box, Flex } from '@radix-ui/themes';
import * as Sentry from '@sentry/react';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { BpSummaryDialog } from 'components/common/dialogs/bp-summary';
import { SafetyPolicyDialog } from 'components/common/dialogs/safety-policy';
import { TermsOfUseDialog } from 'components/common/dialogs/terms-of-use';
import { ErrorBoundary } from 'components/common/error-boundary';
import { VideosCrudDialog } from 'components/common/video/crud-dialog';
import { Content } from 'components/main/content';
import { Maintenance } from 'components/main/landing/forms/maintenance';
import { Login } from 'components/main/landing/login';
import { AutoResetMs } from 'components/main/listeners/auto-reset-ms';
import { ListLibraryListeners } from 'components/main/listeners/list-library-listeners';
import { MachineResponseListener } from 'components/main/listeners/machine-response';
import { NotificationListener } from 'components/main/listeners/notification';
import { QueueListenerHoC } from 'components/main/listeners/queue';
import { Sidebar } from 'components/main/sidebar';
import { PlatformConsoleLayout } from 'components/platform-console';
import env from 'config';
import { MachineModelsProvider } from 'contexts/admin/machine-models.context';
import { GameDataProvider } from 'contexts/game-data.context';
import { MachineProvider } from 'contexts/machine.context';
import { SessionEventsProvider } from 'contexts/session-events.context';
import { SidebarState } from 'enums/cookies.enums';
import useSyncHitters from 'hooks/hitters/useSyncHitters';
import { useBindRefreshHandlers } from 'hooks/useBindRefreshHandlers';
import useCustomResponseListener from 'hooks/useCustomResponseListener';
import { useGameStatusListeners } from 'hooks/useGameStatusListeners';
import { useIsPlatformConsoleUser } from 'hooks/useIsPlatformConsoleUser';
import useLogout from 'hooks/useLogout';
import useReconnectWs from 'hooks/useReconnectWs';
import useResumeLogin from 'hooks/useResumeLogin';
import useVideosListener from 'hooks/useVideosListener';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LanguageCode } from 'lib_ts/enums/translation';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { paths } from 'routes/paths';
import { updateSentrySession } from 'sentry';
import { TermsService } from 'services/terms.service';
import {
  safetyPolicyAcceptanceRequired,
  useAuthStore,
} from 'stores/use-auth-store';
import useBpSummaryStore from 'stores/use-bp-summary-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import i18n from 'translations/i18n';

const AuthorizedMain = () => {
  const app = useCookiesStore((s) => s.app);
  const open = useBpSummaryStore((s) => s.open);

  const { logout } = useLogout();

  const showSafetyPolicy = useAuthStore((s) => s.showSafetyPolicy);
  const showTermsOfUse = useAuthStore((s) => s.showTermsOfUse);

  useVideosListener();
  useCustomResponseListener();

  // ? hitters need to be loaded for use outside of hitter library
  useSyncHitters();

  return (
    <ErrorBoundary identifier="AuthorizedMain" hideIntercom>
      <SessionEventsProvider>
        <MachineProvider identifier="MachineCx">
          <ListLibraryListeners />

          <GameDataProvider>
            <MachineModelsProvider>
              <AutoResetMs />
              <Flex
                className="Main"
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                {/* sidebar */}
                <Box
                  className={StringHelper.classNames([
                    'MainSidebar',
                    app.sidebar_state === SidebarState.full ? 'full' : 'mini',
                  ])}
                >
                  <Sidebar />
                </Box>
                {/* content */}
                <Box className="MainContent" flexGrow="1">
                  <Content />
                </Box>
              </Flex>
              <QueueListenerHoC />
              <MachineResponseListener />

              {showSafetyPolicy && (
                <SafetyPolicyDialog
                  logoutFn={() => logout({ reason: 'logout' })}
                  show={showSafetyPolicy}
                  onClose={async (accepted: boolean) => {
                    if (accepted) {
                      /** date will be updated, let the user proceed once saved */
                      await TermsService.getInstance().accept();

                      useAuthStore.getState().setShowSafetyPolicy(false);
                      return;
                    }

                    /** check whether an existing (previous) acceptance date exists and is still valid */
                    const ds = await TermsService.getInstance().check();

                    if (safetyPolicyAcceptanceRequired(ds)) {
                      NotifyHelper.warning({
                        message_md:
                          'You must accept the terms of service before proceeding.',
                      });
                      return;
                    }

                    /** existing (previous) acceptance date is still valid */
                    useAuthStore.getState().setShowSafetyPolicy(false);
                  }}
                />
              )}

              {showTermsOfUse && (
                <TermsOfUseDialog
                  show={showTermsOfUse}
                  onClose={() =>
                    useAuthStore.getState().setShowTermsOfUse(false)
                  }
                />
              )}
            </MachineModelsProvider>
          </GameDataProvider>
        </MachineProvider>
      </SessionEventsProvider>

      {open && <BpSummaryDialog identifier="BpSummaryDialog" />}

      <VideosCrudDialog />
    </ErrorBoundary>
  );
};
const UnauthorizedMain = () => (
  <ErrorBoundary identifier="UnauthorizedMain" hideIntercom>
    <NotificationListener />
    <Login />
  </ErrorBoundary>
);

export const Main = () => {
  /** Initial App Setup */
  const current = useAuthStore((s) => s.current);
  const resumingLogin = useAuthStore((s) => s.resumingLogin);

  const { pathname } = useLocation();
  const isPlatformConsoleUser = useIsPlatformConsoleUser();

  // Auth0 logout is hook-only; bind refresh callbacks after mount
  useBindRefreshHandlers();

  // attempt to resume Auth0 or Legacy login sessions
  useResumeLogin();

  // manage WS reconnects on certain browser events
  useReconnectWs({ enabled: !resumingLogin });

  // restrict app functionality if we're mid-game
  useGameStatusListeners();

  useEffect(() => {
    // show a toast asking the user to logout/refresh if the app version has changed
    useAuthStore.getState().checkAppVersion();
  }, []);

  // set user context for Sentry
  useEffect(() => {
    Sentry.setUser({ id: current.userID, email: current.email });
  }, [current.userID, current.email]);

  useEffect(() => {
    updateSentrySession({
      wsID: current.wsID,
      machineID: current.machineID,
    });

    Sentry.setTags({
      wsID: current.wsID,
      machineID: current.machineID,
    });
  }, [current.wsID, current.machineID]);

  useEffect(() => {
    if (!current.language) {
      // default and reset behaviour
      if (i18n.language !== LanguageCode.English) {
        // only update if necessary
        i18n.changeLanguage(LanguageCode.English);
      }
      return;
    }

    const nextLanguage = env.enable.change_language
      ? current.language
      : LanguageCode.English;

    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage);
    }
  }, [current.language]);

  // wait until we've finished determing the login status before proceeding
  if (resumingLogin) {
    return null;
  }

  if (env.maintenance) {
    return <Maintenance />;
  }

  if (current.auth && pathname.startsWith(paths.platformConsole.root)) {
    // Platform Console renders its own standalone layout (no sidebar chrome)
    return isPlatformConsoleUser ? (
      <PlatformConsoleLayout />
    ) : (
      <Navigate to={paths.home} replace />
    );
  }

  if (current.auth) {
    return <AuthorizedMain />;
  }

  return <UnauthorizedMain />;
};
